import type { AsyncFlow, AsyncFlowState, MutableAsyncFlow } from '../contract/async-flow';
import type { FlowSubscription } from '../contract/subscription';
import { withBypassSsr } from '../utils/bypassSsrCheck';
import { MutableFlowImpl } from './create-flow';

/**
 * Проверяет структурную эквивалентность двух состояний {@link AsyncFlowState}.
 *
 * Сравнение сравнивает три поля дискриминированного union'а — `status`, `data`,
 * `error` — через {@link Object.is}. Этого достаточно, чтобы определить, что
 * поток не поменял своего состояния семантически: сам тип `AsyncFlowState`
 * не содержит других полей.
 */
function isSameAsyncFlowState<Data>(a: AsyncFlowState<Data>, b: AsyncFlowState<Data>): boolean {
    if (a.status !== b.status) {
        return false;
    }
    if (!Object.is(a.data, b.data)) {
        return false;
    }
    if (a.status === 'error' && b.status === 'error') {
        return Object.is(a.error, b.error);
    }
    return true;
}

/**
 * Реализация MutableAsyncFlow.
 *
 * Этот класс расширяет базовую реализацию Flow для обработки асинхронных операций с тремя возможными состояниями:
 * - `pending`: Асинхронная операция выполняется
 * - `success`: Асинхронная операция завершена успешно
 * - `error`: Асинхронная операция завершилась с ошибкой
 *
 * @typeParam Data - Тип данных, которые будут получены при успешном завершении асинхронной операции
 */
export class MutableAsyncFlowImpl<Data>
    extends MutableFlowImpl<AsyncFlowState<Data>>
    implements MutableAsyncFlow<Data>
{
    /**
     * Кэшированный promise, возвращаемый из asPromise()
     */
    private promise: Promise<Data> | null = null;

    /**
     * Отправляет новое состояние асинхронного потока, уведомляя всех подписчиков и управляя внутренним кэшем promise.
     *
     * Если новое состояние shallow-эквивалентно текущему (совпадают `status`,
     * `data` и `error` — сравнение через {@link Object.is}), метод сразу
     * выходит: не сбрасывает закешированный promise и не уведомляет
     * подписчиков.
     *
     * @param value - Новое состояние асинхронного потока для отправки. Может быть:
     *   - `{ status: "pending" }` - Указывает, что асинхронная операция выполняется
     *   - `{ status: "success", data: Data }` - Указывает на успешное завершение
     *   - `{ status: "error", error: unknown }` - Указывает на сбой с ошибкой
     *
     * @example
     * Базовые переходы состояний:
     * ```typescript
     * const asyncFlow = createAsyncFlow<string>({ status: "pending" });
     *
     * // Начать асинхронную операцию
     * asyncFlow.emit({ status: "pending" });
     *
     * // Завершить успешно
     * asyncFlow.emit({ status: "success", data: "Hello World" });
     *
     * // Или обработать ошибку
     * asyncFlow.emit({ status: "error", error: new Error("Something went wrong") });
     * ```
     */
    public override emit(value: AsyncFlowState<Data>): void {
        if (isSameAsyncFlowState(this.value, value)) {
            return;
        }

        if (this.value.status !== 'pending') {
            this.promise = null;
        }

        super.emit(value);
    }

    /**
     * Возвращает promise, который разрешается с данными, когда асинхронный поток достигает состояния успеха,
     * или отклоняется с ошибкой, когда асинхронный поток достигает состояния ошибки.
     *
     * Если текущее состояние уже разрешено (успех или ошибка), promise разрешается/отклоняется немедленно.
     * Если текущее состояние - ожидание, метод подписывается на изменения состояния и ждет разрешения.
     *
     * @returns Promise, который разрешается с данными при успехе или отклоняется с ошибкой при сбое
     *
     * @example
     * ```typescript
     * const asyncFlow = createAsyncFlow<string>({ status: "pending" });
     *
     * // Будет ждать разрешения потока
     * asyncFlow.asPromise()
     *   .then(data => console.log('Success:', data))
     *   .catch(error => console.error('Error:', error));
     *
     * // Отправить состояние успеха
     * asyncFlow.emit({ status: "success", data: "Hello World" });
     * ```
     */
    public asPromise(): Promise<Data> {
        if (this.promise) {
            return this.promise;
        }

        let resolvePromise!: (data: Data) => void;
        let rejectPromise!: (error: unknown) => void;
        const promise = new Promise<Data>((resolve, reject) => {
            resolvePromise = resolve;
            rejectPromise = reject;
        });

        // Кладем promise в кеш до подписки, а не после: изнутри subscribe() поток может синхронно
        // пройти pending -> success -> pending, и сброс кеша на последнем переходе обязан попасть
        // в настоящий объект. Иначе сброс пришелся бы на еще не присвоенное поле, а в кеше потом
        // осел бы разрешенный promise потока, который снова pending. По той же причине
        // вызов, пришедший на этом синхронном переходе, получает этот же объект, а не второй promise.
        this.promise = promise;

        const state = this.getSnapshot();

        if (state.status === 'success') {
            resolvePromise(state.data);
            return promise;
        }

        if (state.status === 'error') {
            rejectPromise(state.error);
            return promise;
        }

        // Уведомление может прийти синхронно изнутри subscribe(), когда объекта подписки еще
        // нет: контракт Flow это разрешает. Поэтому слушатель только помечает подписку
        // ненужной, а снимает ее тот, у кого объект уже есть.
        let subscription: FlowSubscription | null = null;
        let settled = false;

        const onChange = () => {
            const state = this.getSnapshot();

            if (state.status === 'pending') {
                return;
            }

            settled = true;
            subscription?.unsubscribe();

            if (state.status === 'error') {
                rejectPromise(state.error);
                return;
            }

            resolvePromise(state.data);
        };

        subscription = withBypassSsr(() => this.subscribe(onChange));

        if (settled) {
            subscription.unsubscribe();
        }

        // Возвращаем локальный объект, а не поле: поток мог синхронно уйти в новый pending и
        // сбросить кеш, но вызвавший asPromise() ждет именно этот promise.
        return promise;
    }

    /**
     * Преобразует этот MutableAsyncFlow в readonly AsyncFlow.
     *
     * Возвращаемый AsyncFlow предоставляет те же реактивные возможности,
     * но без возможности отправлять новые значения. Это полезно для предоставления
     * readonly доступа к значению потока при сохранении контроля над мутациями.
     *
     * @returns Readonly AsyncFlow
     *
     * @example
     * ```typescript
     * const mutableFlow = createAsyncFlow<number>({ status: "pending" });
     * const readOnlyFlow = mutableFlow.asFlow();
     *
     * // Чтение и подписка работают
     * const snapshot = readOnlyFlow.getSnapshot();
     * const unsubscribe = readOnlyFlow.subscribe(callback);
     *
     * // Мутации недоступны
     * // readOnlyFlow.emit({ status: "success", data: 42 }); // ❌ Ошибка
     * ```
     */
    public override asFlow(): AsyncFlow<Data> {
        return this;
    }
}

/**
 * Создает новый MutableAsyncFlow с указанным начальным состоянием.
 *
 * @typeParam Data - Тип данных, которые будут доступны при успешном завершении асинхронной операции
 *
 * @param initialValue - Начальное состояние асинхронного потока. Может быть:
 *   - `{ status: "pending" }` - Для операций, которые еще не завершены
 *   - `{ status: "success", data: Data }` - Для операций, которые завершились успешно
 *   - `{ status: "error", error: unknown }` - Для операций, которые завершились с ошибкой
 *
 * @returns Новый экземпляр MutableAsyncFlow, инициализированный заданным значением
 *
 * @example
 * Базовое использование с состоянием ожидания:
 * ```typescript
 * const userFlow = createAsyncFlow<User>({ status: "pending" });
 *
 * // Подписаться на изменения состояния
 * userFlow.subscribe((state) => {
 *   if (state.status === "success") {
 *     console.log("User loaded:", state.data);
 *   } else if (state.status === "error") {
 *     console.error("Failed to load user:", state.error);
 *   }
 * });
 *
 * // Симулировать асинхронную операцию
 * fetchUser()
 *   .then(user => userFlow.emit({ status: "success", data: user }))
 *   .catch(error => userFlow.emit({ status: "error", error }));
 * ```
 *
 * @example
 * Начало с состояния успеха:
 * ```typescript
 * const cachedDataFlow = createAsyncFlow<string>({
 *   status: "success",
 *   data: "cached value"
 * });
 *
 * // Синхронное чтение данных:
 * const snapshot = cachedDataFlow.getSnapshot();
 * if (snapshot.status === "success") {
 *   console.log(snapshot.data); // "cached value"
 * }
 * ```
 *
 * @example
 * Использование с async/await:
 * ```typescript
 * const apiFlow = createAsyncFlow<ApiResponse>({ status: "pending" });
 *
 * async function loadData() {
 *   try {
 *     apiFlow.emit({ status: "pending" });
 *     const response = await fetch('/api/data');
 *     const data = await response.json();
 *     apiFlow.emit({ status: "success", data });
 *   } catch (error) {
 *     apiFlow.emit({ status: "error", error });
 *   }
 * }
 *
 * // Дождаться, когда данные станут доступны
 * const data = await apiFlow.asPromise();
 * ```
 */
export function createAsyncFlow<Data>(initialValue: AsyncFlowState<Data>): MutableAsyncFlow<Data> {
    return new MutableAsyncFlowImpl(initialValue);
}
